import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import {
  Sparkles, Loader2, Building2, Tag, ArrowRight,
  CheckCircle, XCircle, Eye, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiUrl } from '@/lib/api';

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  VALUABLE: { label: 'New', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  DELIVERED: { label: 'Delivered', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  ACCEPTED: { label: 'Accepted', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  REJECTED: { label: 'Rejected', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
};

export default function MerchantRecommendationsPage() {
  const { token } = useAuth();
  const [pitches, setPitches] = useState<unknown[]>([]);
  const [stats, setStats] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pitchesRes, statsRes] = await Promise.all([
        fetch(getApiUrl('/api/v2/merchant/engagement/pitches?limit=50'), { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(getApiUrl('/api/v2/merchant/engagement/dashboard'), { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      if (pitchesRes.ok) setPitches(await pitchesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-emerald-400';
    if (score >= 50) return 'text-blue-400';
    return 'text-zinc-400';
  };

  const pitchList = pitches as Array<{ id: string; product_name?: string; vendor_name?: string; category?: string; status_v2?: string; effective_score?: number; pitch_content?: string; admin_notes?: string }>;
  const statsObj = stats as { total_valuable?: number; total_delivered?: number; total_accepted?: number; total_rejected?: number; average_score?: number } | null;
  const filteredPitches = filterStatus === 'all'
    ? pitchList
    : pitchList.filter(p => p.status_v2 === filterStatus);

  const filterOptions = [
    { value: 'all', label: 'All', count: pitchList.length },
    { value: 'VALUABLE', label: 'New', count: pitchList.filter(p => p.status_v2 === 'VALUABLE').length },
    { value: 'DELIVERED', label: 'Delivered', count: pitchList.filter(p => p.status_v2 === 'DELIVERED').length },
    { value: 'ACCEPTED', label: 'Accepted', count: pitchList.filter(p => p.status_v2 === 'ACCEPTED').length },
    { value: 'REJECTED', label: 'Rejected', count: pitchList.filter(p => p.status_v2 === 'REJECTED').length },
  ];

  if (loading) {
    return (
      <MerchantLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-600" />
        </div>
      </MerchantLayout>
    );
  }

  return (
    <MerchantLayout>
      <div className="space-y-6" data-testid="merchant-recommendations-page">
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500/10 to-rose-600/5 rounded-2xl p-6 border border-rose-500/20">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles className="w-7 h-7 text-rose-400" />
            <h1 className="text-2xl font-bold text-white">Recommendations</h1>
          </div>
          <p className="text-zinc-400">
            Curated vendor pitches evaluated by AI and our expert team
          </p>
          {statsObj && (
            <div className="mt-4 flex items-center gap-6">
              <div className="bg-white/5 rounded-lg px-4 py-2">
                <span className="text-zinc-500 text-xs">Total Pitches</span>
                <p className="text-xl font-bold text-white">{(statsObj.total_valuable ?? 0) + (statsObj.total_delivered ?? 0) + (statsObj.total_accepted ?? 0) + (statsObj.total_rejected ?? 0)}</p>
              </div>
              {statsObj.average_score != null && (
                <div className="bg-white/5 rounded-lg px-4 py-2">
                  <span className="text-zinc-500 text-xs">Avg. Score</span>
                  <p className="text-xl font-bold text-white">{statsObj.average_score}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Status Filter Tabs */}
        <div className="flex gap-1.5 flex-wrap">
          {filterOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilterStatus(opt.value)}
              className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                filterStatus === opt.value
                  ? 'bg-rose-500 text-white'
                  : 'bg-[#18181b] border border-white/5 text-zinc-400 hover:bg-white/5'
              }`}
              data-testid={`filter-${opt.value}`}
            >
              {opt.label} ({opt.count})
            </button>
          ))}
        </div>

        {/* Pitch Cards */}
        {filteredPitches.length === 0 ? (
          <div className="bg-[#18181b] rounded-xl border border-white/5 p-12 text-center">
            <Sparkles className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-base font-medium text-zinc-400 mb-2">No Pitches Found</h3>
            <p className="text-zinc-600 max-w-md mx-auto text-sm">
              {filterStatus === 'all'
                ? 'Evaluated pitches will appear here once our team reviews them.'
                : `No pitches in "${filterStatus}" status.`
              }
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredPitches.map((pitch) => {
              const badge = STATUS_BADGE[pitch.status_v2 ?? ''] || STATUS_BADGE.VALUABLE;
              return (
                <div key={pitch.id} className="bg-[#18181b] rounded-xl border border-white/5 hover:border-white/10 transition-all" data-testid={`recommendation-${pitch.id}`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-base font-semibold text-white">{pitch.product_name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${badge.color}`}>
                            {badge.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-zinc-500">
                          {pitch.vendor_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3.5 h-3.5" />
                              {pitch.vendor_name}
                            </span>
                          )}
                          {pitch.category && (
                            <span className="flex items-center gap-1">
                              <Tag className="w-3.5 h-3.5" />
                              {pitch.category}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Score */}
                      {pitch.effective_score != null && (
                        <div className="text-center flex-shrink-0">
                          <div className={`text-2xl font-bold ${getScoreColor(pitch.effective_score)}`}>
                            {Math.round(pitch.effective_score)}
                          </div>
                          <div className="text-xs text-zinc-600">Score</div>
                        </div>
                      )}
                    </div>

                    {/* Content preview */}
                    <p className="text-zinc-400 text-sm line-clamp-2 mb-4">
                      {pitch.pitch_content}
                    </p>

                    {/* Admin Notes */}
                    {pitch.admin_notes && (
                      <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3 mb-4">
                        <p className="text-sm text-blue-300">
                          <span className="font-medium">Expert Note: </span>
                          {pitch.admin_notes}
                        </p>
                      </div>
                    )}

                    {/* Action */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-zinc-600">
                        {pitch.status_v2 === 'DELIVERED' && (
                          <span className="flex items-center gap-1 text-blue-400">
                            <Eye className="w-3 h-3" /> Needs your decision
                          </span>
                        )}
                        {pitch.status_v2 === 'VALUABLE' && (
                          <span className="flex items-center gap-1 text-emerald-400">
                            <Sparkles className="w-3 h-3" /> New - click to view
                          </span>
                        )}
                      </div>
                      <Link to={`/merchant/pitches/${pitch.id}`}>
                        <Button variant="ghost" className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 text-sm" data-testid={`view-pitch-${pitch.id}`}>
                          View Details <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MerchantLayout>
  );
}
